import {Button, Col, notification, Row, Space, Tabs} from 'antd';
import {CardProps} from 'antd/es/card';
import {FormProps} from 'antd/es/form';
import {PageHeader} from 'components';
import React, {useContext, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {StringParam, useQueryParam} from 'use-query-params';
import {NotFound} from '../NotFound';
import {AffiliateDetails} from './Details/AffiliateDetails';
import {AffiliateTitle} from './AffiliateTitle';
import {BusinessDetails} from './Details/BusinessDetails';
import {PayoutMethodDetails} from './Invoices/PayoutMethodDetails';
import {AffiliateStatusTags} from './AffiliateStatusTags';
import {TabProps} from './utils/types';
import {AffiliateMerchants} from './Merchants/AffiliateMerchants';
import {AffiliateInvoiceHistory} from './Invoices/AffiliateInvoiceHistory';
import {useAffiliatePayoutMethodsQuery} from 'api/generated';
import {useRetrieveAffiliateHistoryMutation} from 'api/generated';
import {useAffiliateQuery} from 'api/generated';
import {UserContext} from 'lib/userContext';

const {TabPane} = Tabs;

export const Affiliate: React.FC<RouteComponentProps<{affiliateId: string}>> = ({match}) => {
  const {affiliateId} = match.params;
  const [_activeTab, setActiveTab] = useQueryParam('tab', StringParam);
  const activeTab = _activeTab || 'details';
  const [retrieveAffiliateHistoric] = useRetrieveAffiliateHistoryMutation();
  const {userEmail} = useContext(UserContext);
  const [exporting, setExporting] = useState(false);

  const {data, loading} = useAffiliateQuery({
    variables: {affiliateId},
    context: {useApolloNetworkStatus: false}
  });

  const {data: payoutMethods, loading: payoutMethodsLoading} = useAffiliatePayoutMethodsQuery({
    variables: {
      affiliateId
    },
    skip: !affiliateId
  });

  const affiliate = data?.affiliate;
  const payoutMethod = payoutMethods?.affiliatePayoutMethods[0];

  if (!affiliate && !loading && !payoutMethod && !payoutMethodsLoading) {
    return (
      <NotFound
        message={
          <>
            Sorry, affiliate with id <strong>{affiliateId}</strong> does not exist
          </>
        }
      />
    );
  }

  const handleExport = async () => {
    try {
      setExporting(true);
      await retrieveAffiliateHistoric({
        variables: {
          affiliateId: affiliate?.id!,
          email: userEmail
        }
      });
    } catch (error) {
      notification.error({
        key: 'export_affiliate_historic_job',
        message: 'Export failed',
        description: 'Has been an error while exporting the affiliate historic, please try again.'
      });
    } finally {
      notification.success({
        key: 'export_affiliate_historic_job',
        message: 'Export successful',
        description: `You will receive the file via email at ${userEmail} within the next few minutes.`
      });
      setExporting(false);
    }
  };

  const cardProps: CardProps = {
    size: 'small',
    loading,
    style: {
      marginBottom: 25
    }
  };

  const narrowFormProps: FormProps = {
    labelAlign: 'left',
    labelCol: {span: 8},
    wrapperCol: {span: 16},
    colon: false
  };

  const tabs: TabProps[] = [
    {
      tab: 'Details',
      key: 'details',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <AffiliateDetails
              affiliate={affiliate}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            <BusinessDetails
              affiliate={affiliate}
              payoutMethod={payoutMethod}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Accounts',
      key: 'accounts',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <AffiliateMerchants affiliate={affiliate} cardProps={cardProps} />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Payouts',
      key: 'payouts',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <AffiliateInvoiceHistory
              affiliate={affiliate}
              payoutMethod={payoutMethod}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            <PayoutMethodDetails
              affiliate={affiliate}
              payoutMethod={payoutMethod}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
          </Col>
        </Row>
      ),
      enabled: true
    }
  ];

  return (
    <>
      <PageHeader loading={loading} backPath={'/affiliates'}>
        <AffiliateTitle affiliate={affiliate} />
        <AffiliateStatusTags affiliate={affiliate} payoutMethod={payoutMethod} />
      </PageHeader>
      <Tabs
        activeKey={activeTab}
        onChange={(tab) => setActiveTab(tab)}
        type="card"
        tabBarExtraContent={
          <Space>
            <Button loading={exporting} onClick={handleExport}>
              Export history
            </Button>
          </Space>
        }>
        {tabs.map(
          (tab) =>
            tab.enabled && (
              <TabPane tab={tab.tab} key={tab.key}>
                {tab.children}
              </TabPane>
            )
        )}
      </Tabs>
    </>
  );
};
